
import {
  computed,
  defineComponent,
  onMounted,
  onUnmounted,
  ref,
  PropType,
  ComponentPropsOptions
} from '@vue/composition-api';
import { HandleProps, HandleElement } from '../../types';
import { Position } from '../../types';
import { useHandle, useNode, useWorkFlow } from '../../composables';
import { getDimensions, isDef, isMouseEvent } from '../../utils';

export default defineComponent<HandleProps>({
  name: 'Handle',

  props: {
    position: {
      type: String,
      default: Position.Top
    },
    connectable: {
      type: Boolean
    },
    connectableStart: {
      type: Boolean,
      default: true
    },
    connectableEnd: {
      type: Boolean,
      default: true
    },
    id: {
      type: String,
      default: null
    },
    type: {
      type: String as PropType<HandleProps['type']>,
      required: true
    }

    // isValidConnection: ValidConnectionFunc;
  } as ComponentPropsOptions<HandleProps>,

  setup(props) {
    const type = computed(() => props.type ?? 'source');

    const isValidConnection = computed(() => null); // props.isValidConnection ??

    const handleId = computed(() => props.id);

    const {
      id: flowId,
      connectionStartHandle,
      connectionClickStartHandle,
      connectionEndHandle,
      workFlowRef,
      nodesConnectable,
      noDragClassName,
      noPanClassName,
      disabled
    } = useWorkFlow();

    const { id: nodeId, node, nodeEl, connectedEdges } = useNode();

    const handle = ref<HTMLDivElement>();

    const isConnectableStart = computed(() =>
      typeof props.connectableStart !== 'undefined'
        ? props.connectableStart
        : true
    );

    const isConnectableEnd = computed(() =>
      typeof props.connectableEnd !== 'undefined' ? props.connectableEnd : true
    );

    const isConnecting = computed(
      () =>
        (connectionStartHandle.value?.nodeId === nodeId &&
          connectionStartHandle.value?.id === handleId.value &&
          connectionStartHandle.value?.type === type.value) ||
        (connectionEndHandle.value?.nodeId === nodeId &&
          connectionEndHandle.value?.id === handleId.value &&
          connectionEndHandle.value?.type === type.value)
    );

    const isClickConnecting = computed(
      () =>
        connectionClickStartHandle.value?.nodeId === nodeId &&
        connectionClickStartHandle.value?.id === handleId.value &&
        connectionClickStartHandle.value?.type === type.value
    );

    const { handlePointerDown, handleClick } = useHandle({
      nodeId,
      handleId: handleId.value,
      isValidConnection,
      type
    });

    const isConnectable = computed(() => {
      if (disabled.value) {
        return false;
      }

      if (
        typeof props.connectable === 'string' &&
        props.connectable === 'single'
      ) {
        return !connectedEdges.value.some(edge => {
          const id = edge[`${type.value}Handle`];

          if (edge[type.value] !== nodeId) {
            return false;
          }

          return id ? id === handleId.value : true;
        });
      }

      if (typeof props.connectable === 'number') {
        return (
          connectedEdges.value.filter(edge => {
            const id = edge[`${type.value}Handle`];

            if (edge[type.value] !== nodeId) {
              return false;
            }

            return id ? id === handleId.value : true;
          }).length < props.connectable
        );
      }

      // if (typeof props.connectable === 'function') {
      //   return props.connectable(node, connectedEdges.value);
      // }

      return isDef(props.connectable)
        ? props.connectable
        : nodesConnectable.value;
    });

    // todo: remove this and have users handle this themselves using `updateNodeInternals`
    // set up handle bounds if they don't exist yet and the node has been initialized (i.e. the handle was added after the node has already been mounted)
    onMounted(() => {
      // if the node isn't initialized yet, we can't set up the handle bounds
      // the handle bounds will be automatically set up when the node is initialized (`updateNodeDimensions`)
      if (!node.dimensions.width || !node.dimensions.height) {
        return;
      }

      const existingBounds = node.handleBounds[type.value]?.find(
        b => b.id === handleId.value
      );

      if (!workFlowRef.value || existingBounds) {
        return;
      }

      const viewportNode = workFlowRef.value.querySelector(
        '.workflow__transformationpane'
      );

      if (!nodeEl.value || !handle.value || !viewportNode || !handleId.value) {
        return;
      }

      const nodeBounds = nodeEl.value.getBoundingClientRect();

      const handleBounds = handle.value.getBoundingClientRect();

      const style = window.getComputedStyle(viewportNode);
      const { m22: zoom } = new window.DOMMatrixReadOnly(style.transform);

      const nextBounds: HandleElement = {
        id: handleId.value,
        position: props.position!,
        x: (handleBounds.left - nodeBounds.left) / zoom,
        y: (handleBounds.top - nodeBounds.top) / zoom,
        type: type.value,
        nodeId,
        ...getDimensions(handle.value)
      };

      node.handleBounds[type.value] = [
        ...(node.handleBounds[type.value] ?? []),
        nextBounds
      ];
    });

    onUnmounted(() => {
      // clean up node internals
      const handleBounds = node.handleBounds[type.value];
      if (handleBounds) {
        node.handleBounds[type.value] = handleBounds.filter(
          b => b.id !== handleId.value
        );
      }
    });

    function onPointerDown(event: MouseEvent | TouchEvent) {
      const isMouseTriggered = isMouseEvent(event);

      if (
        isConnectable.value &&
        isConnectableStart.value &&
        ((isMouseTriggered && event.button === 0) || !isMouseTriggered)
      ) {
        handlePointerDown(event);
      }
    }

    function onClick(event: MouseEvent) {
      if (
        !nodeId ||
        (!connectionClickStartHandle.value && !isConnectableStart.value)
      ) {
        return;
      }

      if (isConnectable.value) {
        handleClick(event);
      }
    }

    return {
      handle,
      onClick,
      onPointerDown,

      isConnectable,
      isConnecting,
      isClickConnecting,
      isConnectableStart,
      isConnectableEnd,

      flowId,
      nodeId,
      handleId,
      disabled,
      noPanClassName,
      noDragClassName
    };
  }
});
